import { useMemo } from 'react';

import { ApiEndpoint } from '@/api/registry';
import { useApiQuery } from '@/api/useApiQuery';
import { BFG_MODEL_CONFIG } from '@/features/forecasts/config/fctRuns';
import { useEvaluationQuery, type EvaluationRow } from '@/features/forecasts/hooks/useForecastEvaluation';
import { useTenant } from '@/providers/TenantProvider';

// Landing-page "forecast change" table. For each DFU pair (product × customer):
//   Δ   = Mean Signed Error           (eval endpoint, batched across all DFUs)
//   Δ%  = Mean Signed Percent Error   (eval endpoint, batched across all DFUs)
//   Current Mean (T) / Last Locked Mean (T-1) = BFG forecast mean, per-DFU
//     (the forecast endpoint has no multi-key support, so one call per DFU/run).
//
// The eval run id is HARDCODED: the eval job emits its own run id (distinct from
// the forecast run the solutions service resolves), so there's no dynamic lookup
// for it. The forecast means DO resolve via the solutions service per fct.
// TODO(engineer): hardcoded eval run UUID for T = 2025-05-04 — the eval job emits its
// own run id with no dynamic lookup path (unlike the forecast means, which resolve via
// the solutions service). Follow-up: add eval-run resolution by fct to the solutions
// service so the home page doesn't pin to a single dated run.
const HOME_T_EVAL_RUN_ID = '272504c0-6892-4bcf-ac39-6969744083f7';

// BFG forecast config for the two means (resolved via the solutions service).
const HOME_T_FCT = '2025-05-04';
const HOME_TM1_FCT = '2025-04-27';
const HOME_T_LEAD_TIME = 0;
const HOME_TM1_LEAD_TIME = 7;

const HOME_SPAN_DAYS = 28;

// Grain keys for the evaluation primary_keys (match the eval file schema).
const PRODUCT_KEY = 'product_id';
const CUSTOMER_KEY = 'ship_to_cust_id';
const REGION_KEY = 'ship_to_region';
const DEFAULT_REGION = 'North America';

export type DfuPair = { product: string; customer: string };

// The DFU pairs to surface on the home page (hardcoded landing set).
export const HOME_DFU_PAIRS: DfuPair[] = [
  { product: 'prod_ADD28545A7EC', customer: 'cust_14A58BABC05F' },
  { product: 'prod_C9A3FFF2FB63', customer: 'cust_14A58BABC05F' },
  { product: 'prod_C6AB7DEEF2D2', customer: 'cust_310489FC647A' },
  { product: 'prod_734E860B9DBB', customer: 'cust_7394A1DCFDFD' },
  { product: 'prod_29FBEF22554C', customer: 'cust_618A9FF5B9EE' },
  { product: 'prod_8FBC1DB5BE2A', customer: 'cust_618A9FF5B9EE' },
  { product: 'prod_3418776E8F5A', customer: 'cust_310489FC647A' },
  { product: 'prod_90C97780179C', customer: 'cust_310489FC647A' },
  { product: 'prod_02461FE264F2', customer: 'cust_D38C3884ACB9' },
  { product: 'prod_DD712278BA1E', customer: 'cust_5C1EDC024CEF' },
  { product: 'prod_662D9A891344', customer: 'cust_A81E0D77AC76' },
  { product: 'prod_0D67B56FA7B7', customer: 'cust_3287C5E02130' },
  { product: 'prod_45B70DDE78D8', customer: 'cust_0C3CD133201A' },
  { product: 'prod_F8CDC2B0D0C6', customer: 'cust_50A1DEF9FA7F' },
  { product: 'prod_D68579028730', customer: 'cust_50A1DEF9FA7F' },
  { product: 'prod_894B0EE7BB93', customer: 'cust_2AAA1B24233F' },
  { product: 'prod_8FBC1DB5BE2A', customer: 'cust_2AAA1B24233F' },
  { product: 'prod_4CDAF385B0F5', customer: 'cust_3287C5E02130' },
  { product: 'prod_E1102CD44447', customer: 'cust_99FE2AF20640' },
  { product: 'prod_60240496E66C', customer: 'cust_14A58BABC05F' },
  { product: 'prod_3460B5523219', customer: 'cust_5C1EDC024CEF' },
  { product: 'prod_18AC83822CAD', customer: 'cust_6D756844F67D' },
  { product: 'prod_712A0F6E6C65', customer: 'cust_3287C5E02130' },
  { product: 'prod_7E28DB1AFD64', customer: 'cust_EB7E43FC28BF' },
  { product: 'prod_DCF5A14B4407', customer: 'cust_94E8DD26B9C8' },
  { product: 'prod_31209143DA02', customer: 'cust_6D756844F67D' },
  { product: 'prod_683DF41E19E2', customer: 'cust_2AAA1B24233F' },
  { product: 'prod_8EA919F27C81', customer: 'cust_94E8DD26B9C8' },
  { product: 'prod_AEAD660BA8A3', customer: 'cust_310489FC647A' },
  { product: 'prod_8CDB701B75F4', customer: 'cust_E1149D396E3C' },
  { product: 'prod_905D36F816A8', customer: 'cust_94E8DD26B9C8' },
  { product: 'prod_7584B468B832', customer: 'cust_3287C5E02130' },
  { product: 'prod_93A8E5B22230', customer: 'cust_310489FC647A' },
  { product: 'prod_52DE06A67807', customer: 'cust_618A9FF5B9EE' },
  { product: 'prod_479D1D4A59E0', customer: 'cust_D38C3884ACB9' },
  { product: 'prod_5FEF0C779026', customer: 'cust_3287C5E02130' },
  { product: 'prod_1159313F33D3', customer: 'cust_310489FC647A' },
  { product: 'prod_340F99164E5A', customer: 'cust_310489FC647A' },
  { product: 'prod_4CDAF385B0F5', customer: 'cust_618A9FF5B9EE' },
  { product: 'prod_FBE6D696DDD0', customer: 'cust_618A9FF5B9EE' },
  { product: 'prod_EEB5C16D3E74', customer: 'cust_14A58BABC05F' },
  { product: 'prod_4A99168A7D96', customer: 'cust_618A9FF5B9EE' },
  { product: 'prod_FD106D63FCA9', customer: 'cust_1B3F0A8A4D66' },
  { product: 'prod_CAB30B172BEC', customer: 'cust_310489FC647A' },
  { product: 'prod_74D1CE29460E', customer: 'cust_310489FC647A' },
  { product: 'prod_F02990A0B2BD', customer: 'cust_1B3F0A8A4D66' },
  { product: 'prod_6CD2FBCC96BB', customer: 'cust_14A58BABC05F' },
  { product: 'prod_7928ECEEA6CD', customer: 'cust_618A9FF5B9EE' },
  { product: 'prod_19556C67203D', customer: 'cust_3287C5E02130' },
  { product: 'prod_3649BE285B50', customer: 'cust_14A58BABC05F' },
  { product: 'prod_79B200E0B8DD', customer: 'cust_3287C5E02130' },
  { product: 'prod_9D689650A7B6', customer: 'cust_14A58BABC05F' },
  { product: 'prod_0F753CF0B1ED', customer: 'cust_310489FC647A' },
  { product: 'prod_F8E347A00DE5', customer: 'cust_618A9FF5B9EE' },
  { product: 'prod_7B2DBC772BB2', customer: 'cust_6D756844F67D' },
  { product: 'prod_A2245B383C6E', customer: 'cust_14A58BABC05F' },
  { product: 'prod_B36A52B9103F', customer: 'cust_310489FC647A' },
  { product: 'prod_19A916A9F3B3', customer: 'cust_14A58BABC05F' },
  { product: 'prod_CEE1A3CB584E', customer: 'cust_14A58BABC05F' },
  { product: 'prod_6A5FF44AF3E2', customer: 'cust_14A58BABC05F' },
  { product: 'prod_92CD1C17806C', customer: 'cust_14A58BABC05F' },
  { product: 'prod_680B43E53F2F', customer: 'cust_310489FC647A' },
  { product: 'prod_281FE4BCC501', customer: 'cust_310489FC647A' },
  { product: 'prod_7EF4CF0FBC61', customer: 'cust_D38C3884ACB9' },
  { product: 'prod_8247033A5194', customer: 'cust_14A58BABC05F' },
  { product: 'prod_542547805A30', customer: 'cust_3287C5E02130' },
  { product: 'prod_61F705D786CB', customer: 'cust_E1149D396E3C' },
  { product: 'prod_896B22B0D6DE', customer: 'cust_310489FC647A' },
  { product: 'prod_8BEEC240CD51', customer: 'cust_94E8DD26B9C8' },
  { product: 'prod_7308DF48624E', customer: 'cust_EB7E43FC28BF' },
  { product: 'prod_5AEBACCBB466', customer: 'cust_310489FC647A' },
  { product: 'prod_BB1698393ECF', customer: 'cust_3287C5E02130' },
  { product: 'prod_AE6D5B56BBCC', customer: 'cust_A81E0D77AC76' },
  { product: 'prod_00D2B533A39B', customer: 'cust_310489FC647A' },
  { product: 'prod_AF26915FC963', customer: 'cust_45631FD1A202' },
  { product: 'prod_7A7CD53AE2F5', customer: 'cust_14A58BABC05F' },
  { product: 'prod_A10AAAE69B76', customer: 'cust_14A58BABC05F' },
  { product: 'prod_96777BCDF40D', customer: 'cust_31459B37DA37' },
  { product: 'prod_1BF57156ACD7', customer: 'cust_310489FC647A' },
  { product: 'prod_24F9BAFF2751', customer: 'cust_99FE2AF20640' },
  { product: 'prod_0BCCC7FD28B7', customer: 'cust_0C3CD133201A' },
  { product: 'prod_5FEF0C779026', customer: 'cust_99FE2AF20640' },
  { product: 'prod_19E9852B7E5A', customer: 'cust_6D756844F67D' },
  { product: 'prod_3F022D47B8C9', customer: 'cust_94E8DD26B9C8' },
  { product: 'prod_7E1AE51BEF7D', customer: 'cust_EB7E43FC28BF' },
  { product: 'prod_16D0CEABD506', customer: 'cust_14A58BABC05F' },
  { product: 'prod_7641A2B12ADD', customer: 'cust_94E8DD26B9C8' },
  { product: 'prod_B49F53769379', customer: 'cust_E1149D396E3C' },
  { product: 'prod_1BF57156ACD7', customer: 'cust_3287C5E02130' },
  { product: 'prod_0196C8A7A18F', customer: 'cust_618A9FF5B9EE' },
  { product: 'prod_470DB1CC70E4', customer: 'cust_1B3F0A8A4D66' },
  { product: 'prod_2EC32711169D', customer: 'cust_46519E88A240' },
  { product: 'prod_221F4752002E', customer: 'cust_94E8DD26B9C8' },
  { product: 'prod_586CAA75BA37', customer: 'cust_618A9FF5B9EE' },
  { product: 'prod_A692072B139C', customer: 'cust_618A9FF5B9EE' },
  { product: 'prod_470DB1CC70E4', customer: 'cust_618A9FF5B9EE' },
  { product: 'prod_BE4BEFEDFAA5', customer: 'cust_14A58BABC05F' },
  { product: 'prod_6AD7D42ACD61', customer: 'cust_618A9FF5B9EE' },
  { product: 'prod_561FA8793D28', customer: 'cust_618A9FF5B9EE' },
  { product: 'prod_D997DB010A95', customer: 'cust_3287C5E02130' },
  { product: 'prod_2CEF3BB42D14', customer: 'cust_14A58BABC05F' },
  { product: 'prod_F6343CDF4EBE', customer: 'cust_14A58BABC05F' },
  { product: 'prod_281FE4BCC501', customer: 'cust_618A9FF5B9EE' },
  { product: 'prod_883471AA5F48', customer: 'cust_310489FC647A' },
  { product: 'prod_999A941D6490', customer: 'cust_6D756844F67D' },
  { product: 'prod_3A015B0ECAD7', customer: 'cust_3287C5E02130' },
  { product: 'prod_3E175A9275B6', customer: 'cust_D38C3884ACB9' },
  { product: 'prod_186386C5F778', customer: 'cust_14A58BABC05F' },
  { product: 'prod_62816AB6D012', customer: 'cust_3287C5E02130' },
  { product: 'prod_62816AB6D012', customer: 'cust_618A9FF5B9EE' },
  { product: 'prod_470CF6030343', customer: 'cust_E1149D396E3C' },
  { product: 'prod_91ACEDF5FAB1', customer: 'cust_3287C5E02130' },
  { product: 'prod_EC0C5FA53117', customer: 'cust_3287C5E02130' },
  { product: 'prod_09204766980F', customer: 'cust_14A58BABC05F' },
  { product: 'prod_BD329C556C27', customer: 'cust_3287C5E02130' },
  { product: 'prod_2E7B5CCFE4CF', customer: 'cust_14A58BABC05F' },
  { product: 'prod_19B971ACA67D', customer: 'cust_3287C5E02130' },
  { product: 'prod_29FE66E7564B', customer: 'cust_310489FC647A' },
  { product: 'prod_07E21F866FE7', customer: 'cust_14A58BABC05F' },
  { product: 'prod_5FEF0C779026', customer: 'cust_14A58BABC05F' },
  { product: 'prod_5BD8B2AFC449', customer: 'cust_14A58BABC05F' },
  { product: 'prod_17109802C2C0', customer: 'cust_6D756844F67D' },
  { product: 'prod_FA34047E4289', customer: 'cust_14A58BABC05F' },
  { product: 'prod_C6CC02891B45', customer: 'cust_14A58BABC05F' },
  { product: 'prod_883471AA5F48', customer: 'cust_14A58BABC05F' },
  { product: 'prod_61B3C416256C', customer: 'cust_99FE2AF20640' },
  { product: 'prod_683DF41E19E2', customer: 'cust_7F92658A01C6' },
  { product: 'prod_2DA233D41708', customer: 'cust_3287C5E02130' },
  { product: 'prod_29D4F7251135', customer: 'cust_3287C5E02130' },
  { product: 'prod_8D2189942E80', customer: 'cust_D38C3884ACB9' },
  { product: 'prod_16D0CEABD506', customer: 'cust_99FE2AF20640' },
  { product: 'prod_8CF2418691CC', customer: 'cust_99FE2AF20640' },
  { product: 'prod_31209143DA02', customer: 'cust_618A9FF5B9EE' },
  { product: 'prod_F8531782B957', customer: 'cust_14A58BABC05F' },
  { product: 'prod_281FE4BCC501', customer: 'cust_D38C3884ACB9' },
  { product: 'prod_22D83BB35EFB', customer: 'cust_3287C5E02130' },
  { product: 'prod_712A0F6E6C65', customer: 'cust_99FE2AF20640' },
  { product: 'prod_19B971ACA67D', customer: 'cust_2AAA1B24233F' },
  { product: 'prod_6D750AC22FB7', customer: 'cust_D38C3884ACB9' },
  { product: 'prod_8DA2FEB7CF09', customer: 'cust_99FE2AF20640' },
  { product: 'prod_00015D4C341C', customer: 'cust_310489FC647A' },
  { product: 'prod_0F753CF0B1ED', customer: 'cust_D38C3884ACB9' },
  { product: 'prod_C4ABA3D22E64', customer: 'cust_14A58BABC05F' },
  { product: 'prod_ADD28545A7EC', customer: 'cust_618A9FF5B9EE' },
  { product: 'prod_7C0623F595D6', customer: 'cust_99FE2AF20640' },
  { product: 'prod_246CF6FB6B2C', customer: 'cust_618A9FF5B9EE' },
  { product: 'prod_C3C864DE98DB', customer: 'cust_14A58BABC05F' },
  { product: 'prod_0A0049B9D959', customer: 'cust_14A58BABC05F' },
  { product: 'prod_712A0F6E6C65', customer: 'cust_310489FC647A' },
  { product: 'prod_EEB5C16D3E74', customer: 'cust_D38C3884ACB9' },
  { product: 'prod_C2A485ABA528', customer: 'cust_14A58BABC05F' },
  { product: 'prod_36E25B0E890B', customer: 'cust_310489FC647A' },
  { product: 'prod_FF728E4DEDF6', customer: 'cust_14A58BABC05F' },
  { product: 'prod_FBA87DBAFDDE', customer: 'cust_D38C3884ACB9' },
  { product: 'prod_B6FF1A2CCD53', customer: 'cust_D38C3884ACB9' },
  { product: 'prod_EC6033DBBD3E', customer: 'cust_14A58BABC05F' },
  { product: 'prod_524B12BE0C05', customer: 'cust_14A58BABC05F' },
  { product: 'prod_00015D4C341C', customer: 'cust_94E8DD26B9C8' },
  { product: 'prod_92BAEA761D3A', customer: 'cust_618A9FF5B9EE' },
  { product: 'prod_FC2E4939DD34', customer: 'cust_14A58BABC05F' },
  { product: 'prod_561FA8793D28', customer: 'cust_310489FC647A' },
  { product: 'prod_612E117CE72B', customer: 'cust_310489FC647A' },
  { product: 'prod_2C65C5676290', customer: 'cust_14A58BABC05F' },
  { product: 'prod_3E175A9275B6', customer: 'cust_3287C5E02130' },
  { product: 'prod_4A5B4B56F471', customer: 'cust_3287C5E02130' },
  { product: 'prod_A692072B139C', customer: 'cust_3287C5E02130' },
  { product: 'prod_B47D368782F3', customer: 'cust_3287C5E02130' },
  { product: 'prod_7BC2CDCC189B', customer: 'cust_310489FC647A' },
  { product: 'prod_29D4F7251135', customer: 'cust_6D756844F67D' },
  { product: 'prod_6D52A9B050AD', customer: 'cust_310489FC647A' },
  { product: 'prod_65B9EEF7B515', customer: 'cust_D38C3884ACB9' },
  { product: 'prod_9018B61A84CC', customer: 'cust_14A58BABC05F' },
  { product: 'prod_19E9852B7E5A', customer: 'cust_618A9FF5B9EE' },
  { product: 'prod_B23AD877FEFE', customer: 'cust_A81E0D77AC76' },
];

// The landing page shows a single span — 1 month (28 days).
const MSE_METRIC = `MEAN_SIGNED_ERROR_per_series_span_${HOME_SPAN_DAYS}`; // Δ (forecast − actual, qty)
const MSPE_METRIC = `MEAN_SIGNED_PERCENT_ERROR_per_series_span_${HOME_SPAN_DAYS}`; // Δ %

export function dfuPrimaryKeys(pair: DfuPair): Record<string, string> {
  return {
    [PRODUCT_KEY]: pair.product,
    [CUSTOMER_KEY]: pair.customer,
    [REGION_KEY]: DEFAULT_REGION,
  };
}

// Quantile fan for a single forecast point: P10..P90 in ascending order, used to
// draw the distribution-shape sparklines. null when the row is missing.
export type QuantileFan = number[] | null;

export type HomeForecastChange = {
  delta: number | null; // Mean Signed Error (forecast − actual)
  deltaPercent: number | null; // Mean Signed Percent Error (fraction)
  currentMean: number | null; // BFG forecast mean at T
  lastLockedMean: number | null; // BFG forecast mean at T-1
  currentFan: QuantileFan; // P10..P90 at T
  lastLockedFan: QuantileFan; // P10..P90 at T-1
};

// Percentile keys read for the sparkline fan, ascending.
const FAN_QUANTILE_KEYS = [
  'quantile_10',
  'quantile_20',
  'quantile_30',
  'quantile_50',
  'quantile_70',
  'quantile_80',
  'quantile_90',
] as const;

type ForecastSolutionsListResponse = { forecast_solutions?: Array<{ solution_id?: string }> };
type FetchRunResponse = { run_id?: string };

type ForecastBatchRow = {
  span?: number | null;
  target_output?:
    | ({ mean?: number | null } & Partial<Record<(typeof FAN_QUANTILE_KEYS)[number], number | null>>)
    | null;
  primary_keys?: Record<string, unknown> | null;
};
type ForecastQueryResponse = { results?: ForecastBatchRow[] };

// Pull the ascending P10..P90 fan out of a forecast row's target_output, or null
// if any quantile is missing (a partial fan can't be drawn as a shape).
function fanFromTarget(target: ForecastBatchRow['target_output']): QuantileFan {
  if (!target) return null;
  const fan = FAN_QUANTILE_KEYS.map((k) => target[k]);
  return fan.every((v): v is number => typeof v === 'number') ? fan : null;
}

// One BFG forecast mean per DFU, BATCHED: resolve the production solution for the
// fct → run → a single forecast query OR'ing every DFU's primary keys (the
// forecast endpoint now accepts a primary_keys list and tags each result row
// with its primary_keys). Returns means keyed by pair. Two upstream calls
// (solution + run) + 2 batched forecast requests (DFU keys split in half).
type BfgPoint = { mean: number | null; fan: QuantileFan };

function useBfgMeansBatched(args: { fct: string; leadTime: number; span: number }): {
  byPair: Map<string, BfgPoint>;
  isLoading: boolean;
} {
  const { fct, leadTime, span } = args;
  const { tenantAlias } = useTenant();

  // Split the DFU keys into two roughly-equal halves so the forecast fetch goes
  // out as 2 parallel requests per model instead of one big OR over every pair.
  const [keysA, keysB] = useMemo(() => {
    const all = HOME_DFU_PAIRS.map(dfuPrimaryKeys);
    const mid = Math.ceil(all.length / 2);
    return [all.slice(0, mid), all.slice(mid)] as const;
  }, []);

  const solutions = useApiQuery<ForecastSolutionsListResponse>(tenantAlias, ApiEndpoint.SolutionsForecastList, {
    query: {
      fct,
      model_name: BFG_MODEL_CONFIG.modelName,
      model_version: BFG_MODEL_CONFIG.modelVersion,
      status: 'production',
      limit: 1,
    },
  });
  const solutionId = solutions.data?.forecast_solutions?.[0]?.solution_id ?? null;

  const run = useApiQuery<FetchRunResponse>(
    tenantAlias,
    ApiEndpoint.SolutionsFetchRun,
    { pathParams: { solution_id: solutionId ?? '' } },
    { enabled: Boolean(solutionId) },
  );
  const runId = run.data?.run_id ?? null;

  const forecastBody = (batch: Array<Record<string, string>>) => ({
    body: {
      run_id: runId ?? '',
      file_id: 'forecast.parquet',
      primary_keys: batch,
      leadtime_spans: [[leadTime, span]],
    },
  });
  // Two fixed batches → stable hook order. Each batch is enabled once we have a
  // run id and the batch is non-empty.
  const forecastA = useApiQuery<ForecastQueryResponse>(
    tenantAlias,
    ApiEndpoint.ModelOutputsForecast,
    forecastBody(keysA),
    {
      enabled: Boolean(runId) && keysA.length > 0,
    },
  );
  const forecastB = useApiQuery<ForecastQueryResponse>(
    tenantAlias,
    ApiEndpoint.ModelOutputsForecast,
    forecastBody(keysB),
    {
      enabled: Boolean(runId) && keysB.length > 0,
    },
  );

  const byPair = useMemo(() => {
    const map = new Map<string, BfgPoint>();
    for (const row of [...(forecastA.data?.results ?? []), ...(forecastB.data?.results ?? [])]) {
      const pk = row.primary_keys ?? {};
      const product = pk[PRODUCT_KEY];
      const customer = pk[CUSTOMER_KEY];
      const mean = row.target_output?.mean;
      if (typeof product === 'string' && typeof customer === 'string') {
        map.set(pairKey(product, customer), {
          mean: typeof mean === 'number' ? mean : null,
          fan: fanFromTarget(row.target_output),
        });
      }
    }
    return map;
  }, [forecastA.data, forecastB.data]);

  return {
    byPair,
    isLoading:
      solutions.isLoading ||
      (Boolean(solutionId) && run.isLoading) ||
      (Boolean(runId) && (forecastA.isLoading || forecastB.isLoading)),
  };
}

export type DfuMeans = { currentMean: number | null; lastLockedMean: number | null };

export type HomeForecastChanges = {
  // Per-pair change, keyed by `${product}|${customer}` (see pairKey).
  byPair: Record<string, HomeForecastChange>;
  isLoading: boolean;
  isError: boolean;
};

// Stable per-pair key for matching response rows back to table rows.
export function pairKey(product: string, customer: string): string {
  return `${product}|${customer}`;
}

// Index a metric response's rows by pair, reading the product/customer out of
// each row's primary_keys struct.
function indexByPair(results: EvaluationRow[] | undefined): Map<string, number | null> {
  const map = new Map<string, number | null>();
  for (const row of results ?? []) {
    const pk = row.primary_keys ?? {};
    const product = pk[PRODUCT_KEY];
    const customer = pk[CUSTOMER_KEY];
    if (typeof product === 'string' && typeof customer === 'string') {
      map.set(pairKey(product, customer), typeof row.value === 'number' ? row.value : null);
    }
  }
  return map;
}

// Batched home figures for ALL DFU pairs at the 1-month span — 4 MDS calls total
// regardless of pair count:
//   Δ / Δ%  → 2 evaluation queries (MSE / MSPE), each OR'ing every pair's PKs
//   means   → 1 batched BFG forecast at T (lead 0) + 1 at T-1 (lead 7)
// each split client-side by pair (response rows carry their primary_keys).
export function useHomeForecastChanges(): HomeForecastChanges {
  const primaryKeys = useMemo(() => HOME_DFU_PAIRS.map(dfuPrimaryKeys), []);

  const mse = useEvaluationQuery({ runId: HOME_T_EVAL_RUN_ID, metricName: MSE_METRIC, primaryKeys });
  const mspe = useEvaluationQuery({ runId: HOME_T_EVAL_RUN_ID, metricName: MSPE_METRIC, primaryKeys });

  const current = useBfgMeansBatched({ fct: HOME_T_FCT, leadTime: HOME_T_LEAD_TIME, span: HOME_SPAN_DAYS });
  const lastLocked = useBfgMeansBatched({ fct: HOME_TM1_FCT, leadTime: HOME_TM1_LEAD_TIME, span: HOME_SPAN_DAYS });

  const byPair = useMemo(() => {
    const mseByPair = indexByPair(mse.data?.results);
    const mspeByPair = indexByPair(mspe.data?.results);
    const out: Record<string, HomeForecastChange> = {};
    for (const pair of HOME_DFU_PAIRS) {
      const key = pairKey(pair.product, pair.customer);
      const cur = current.byPair.get(key);
      const prev = lastLocked.byPair.get(key);
      out[key] = {
        delta: mseByPair.get(key) ?? null,
        deltaPercent: mspeByPair.get(key) ?? null,
        currentMean: cur?.mean ?? null,
        lastLockedMean: prev?.mean ?? null,
        currentFan: cur?.fan ?? null,
        lastLockedFan: prev?.fan ?? null,
      };
    }
    return out;
  }, [mse.data, mspe.data, current.byPair, lastLocked.byPair]);

  return {
    byPair,
    isLoading: mse.isLoading || mspe.isLoading || current.isLoading || lastLocked.isLoading,
    isError: mse.isError || mspe.isError,
  };
}
